// src/components/Others/TitleLevelBudget/titleLevelBudget.js
//
// Features:
// 1) View Title Level Budgets in a grid (ISBN, Title, Year, Type, Jan..Dec)
// 2) No checkbox selection, no Edit action — only Delete action per row
// 3) Upload CSV button at top (POST /titlelevelbudget/bulk/upload)
//
// Assumptions based on your API:
// - GET  /titlelevelbudget returns { total, budgets } OR budgets array
// - Each budget has: _id, primaryIsbn, year, type, months{jan..dec}, bookInfo{title}
// - DELETE /titlelevelbudget/:id deletes one record
// - Bulk upload endpoint expects multipart form field name "file"
// - Auth middleware sets req.user.id on backend; UI doesn't need to send createdBy/updatedBy
//
// Re-uses your existing shared components:
// - Heading
// - ORMDataGridV2
// - ORMModal
// - RenderSpinner
// - ormToast

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import APIService from '../../utils/APIService';
import RenderSpinner from '../Spinner';
import ormToast, { TOAST_TYPE } from '../../utils/ormToast';
import Heading from '../Shared/UI/Heading';
import ORMDataGridV2 from '../Shared/Table/ORMDataGridV2';
import ORMModal from '../Shared/UI/ORMModal';
import { getActionColumns } from '../../utils/columnHelper'; // <-- adjust path if needed
import { TITLE_LEVEL_BUDGET_COLUMNS } from 'src/constants/dataGridColumns';

// add this helper near top of file (outside component)
const downloadSampleCsv = () => {
  const sample =
    'primaryIsbn,year,type,jan,feb,mar,apr,may,jun,jul,aug,sep,oct,nov,dec\n' +
    '9780547705200,2026,publishing,100,100,100,100,100,100,100,100,100,100,100,100\n' +
    '9780060000000,2026,publishing,0,0,0,0,0,0,50,50,50,50,50,50\n';

  const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = 'title_level_budget_sample.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
};

const formatBudgetRows = budgets => {
  if (!Array.isArray(budgets)) return [];

  return budgets.map(b => {
    const months = b?.months || {};
    return {
      id: b?._id || b?.id, // DataGrid needs "id"
      _id: b?._id,
      primaryIsbn: b?.primaryIsbn || '',
      title: b?.bookInfo?.title || '',
      year: b?.year,
      type: b?.type || 'publishing',
      jan: months?.jan ?? '',
      feb: months?.feb ?? '',
      mar: months?.mar ?? '',
      apr: months?.apr ?? '',
      may: months?.may ?? '',
      jun: months?.jun ?? '',
      jul: months?.jul ?? '',
      aug: months?.aug ?? '',
      sep: months?.sep ?? '',
      oct: months?.oct ?? '',
      nov: months?.nov ?? '',
      dec: months?.dec ?? '',
    };
  });
};

const TitleLevelBudget = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState({
    isLoading: false,
    budgets: [],
    showDeleteModal: false,
    rowToDelete: null,
    showUploadModal: false,
    uploadMode: 'merge', // merge | replace
    uploadFile: null,
    uploadResult: null,
  });

  const fetchBudgets = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    const response = await APIService.get('/titlelevelbudget');

    if (response?.status === 200) {
      // handle either { budgets: [...] } OR direct array
      const budgetsRaw = Array.isArray(response?.data) ? response.data : response?.data?.budgets || [];
      setState(prev => ({
        ...prev,
        budgets: formatBudgetRows(budgetsRaw),
        isLoading: false,
      }));
    } else {
      setState(prev => ({ ...prev, budgets: [], isLoading: false }));
      ormToast(response?.data?.message || 'Failed to fetch budgets', TOAST_TYPE.ERROR);
    }
  }, []);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  const totalRows = state?.budgets?.length || 0;

  const confirmSingleDelete = useCallback(rowInfo => {
    setState(prev => ({
      ...prev,
      showDeleteModal: true,
      rowToDelete: rowInfo,
    }));
  }, []);

  const handleDelete = useCallback(async () => {
    if (!state?.rowToDelete?._id) return;

    setState(prev => ({ ...prev, isLoading: true }));

    const response = await APIService.delete(`/titlelevelbudget/${state.rowToDelete._id}`);
    if (response?.status === 200) {
      setState(prev => ({
        ...prev,
        budgets: prev.budgets.filter(r => r._id !== state.rowToDelete._id),
        showDeleteModal: false,
        rowToDelete: null,
        isLoading: false,
      }));
      ormToast(response?.data?.message || 'Budget deleted', TOAST_TYPE.SUCCESS);
      // If you cache this route, keep invalidation; otherwise remove
      await APIService.post(`/invalidatecache`, { urls: ['/titlelevelbudget'] });
    } else {
      setState(prev => ({ ...prev, showDeleteModal: false, isLoading: false }));
      ormToast(response?.data?.message || 'Failed to delete budget', TOAST_TYPE.ERROR);
    }
  }, [state?.rowToDelete?._id]);

  const deleteModalContent = useMemo(() => {
    if (!state?.rowToDelete) return '';
    return (
      <Box>
        <Typography variant='body1'>Delete title budget?</Typography>
        <ul>
          <li>
            <strong>{state.rowToDelete.title || '(No title found)'}</strong>
          </li>
          <li>ISBN: {state.rowToDelete.primaryIsbn}</li>
          <li>Year: {state.rowToDelete.year}</li>
          <li>Type: {state.rowToDelete.type}</li>
        </ul>
      </Box>
    );
  }, [state?.rowToDelete]);

  const handleOpenUpload = useCallback(() => {
    setState(prev => ({
      ...prev,
      showUploadModal: true,
      uploadFile: null,
      uploadResult: null,
      uploadMode: 'merge',
    }));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!state.uploadFile) {
      ormToast('Please select a CSV file', TOAST_TYPE.ERROR);
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    const form = new FormData();
    form.append('file', state.uploadFile);

    const response = await APIService.post(`/titlelevelbudget/bulk/upload?mode=${state.uploadMode}`, form);

    if (response?.status === 200) {
      ormToast(response?.data?.message || 'Upload successful', TOAST_TYPE.SUCCESS);

      setState(prev => ({
        ...prev,
        uploadResult: response?.data || null,
        showUploadModal: false,
        isLoading: false,
      }));

      // refresh table
      await fetchBudgets();

      // optional cache invalidation
      await APIService.post(`/invalidatecache`, { urls: ['/titlelevelbudget'] });
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
      ormToast(response?.data?.message || 'Upload failed', TOAST_TYPE.ERROR);
    }
  }, [state.uploadFile, state.uploadMode, fetchBudgets]);

  const columns = useMemo(() => {
    return [
      ...TITLE_LEVEL_BUDGET_COLUMNS,
      getActionColumns([{ name: 'Delete', callbackFunction: confirmSingleDelete }]),
    ];
  }, [confirmSingleDelete]);

  return (
    <Container>
      <Heading heading='Title Level Budget'>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Button variant='contained' onClick={handleOpenUpload}>
            Upload CSV
          </Button>

          <Button variant='outlined' onClick={fetchBudgets}>
            Refresh
          </Button>
        </Stack>
      </Heading>

      {state.isLoading ? (
        <RenderSpinner message='Please wait ... ' />
      ) : (
        <ORMDataGridV2
          columns={columns}
          rows={state.budgets}
          pagination
          // no checkboxSelection
          pinnedColumns={{ right: ['actions'] }}
          height={isMobile ? 'calc(100dvh - 240px)' : 'calc(100dvh - 190px)'}
        >
          <Typography variant='subtitle1'>
            Total Records: <strong style={{ color: 'Red' }}>{totalRows}</strong>
          </Typography>
        </ORMDataGridV2>
      )}

      {state.showDeleteModal && (
        <ORMModal
          open={state.showDeleteModal}
          title='Confirmation'
          primaryButtonName='Delete'
          onClose={() => setState(prev => ({ ...prev, showDeleteModal: false, rowToDelete: null }))}
          handlePrimaryButton={handleDelete}
          content={deleteModalContent}
          maxWidth='sm'
        />
      )}

      {state.showUploadModal && (
        <ORMModal
          open={state.showUploadModal}
          title='Upload CSV'
          primaryButtonName='Upload'
          onClose={() => setState(prev => ({ ...prev, showUploadModal: false }))}
          handlePrimaryButton={handleUpload}
          maxWidth='sm'
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='body2'>
                Upload a CSV with headers: <strong>primaryIsbn, year, type, jan..dec</strong>.{' '}
                <Button
                  variant='text'
                  size='small'
                  onClick={downloadSampleCsv}
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                >
                  Download sample CSV
                </Button>
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography variant='body2' sx={{ minWidth: { sm: 90 } }}>
                  Mode:
                </Typography>
                <select
                  value={state.uploadMode}
                  onChange={e => setState(prev => ({ ...prev, uploadMode: e.target.value }))}
                  style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}
                >
                  <option value='merge'>Merge (blank months ignored)</option>
                  <option value='replace'>Replace (blank months cleared)</option>
                </select>
              </Box>

              <Box>
                <input
                  type='file'
                  accept='.csv,text/csv'
                  onChange={e => setState(prev => ({ ...prev, uploadFile: e.target.files?.[0] || null }))}
                />
                {state.uploadFile?.name && (
                  <Typography variant='caption' sx={{ display: 'block', mt: 1 }}>
                    Selected: <strong>{state.uploadFile.name}</strong>
                  </Typography>
                )}
              </Box>

              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Tip: Use <strong>Merge</strong> if you only want to update provided months. Use <strong>Replace</strong>{' '}
                if missing month values should clear existing data.
              </Typography>
            </Box>
          }
        />
      )}
    </Container>
  );
};

export default TitleLevelBudget;
